import { ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '../../context/I18nContext';
import AnimatedEyebrow from '../AnimatedEyebrow';
import { getHomeData } from './data';
import ImageOnlySkeletonLoader from '../ImageOnlySkeletonLoader';

function renderTitleWithHighlight(item, isActive) {
  const line1 = String(item.titleLine1 || '').trim();
  const line2 = String(item.titleLine2 || '').trim();
  if (line1 || line2) {
    return (
      <span className={`transition-colors duration-500 ${isActive ? 'text-white' : ''}`}>
        {line1 || String(item.title || '')}{' '}
        {line2 ? (
          <span className={`font-bold transition-colors duration-500 ${isActive ? 'text-[#ff5d10]' : ''}`}>
            {line2}
          </span>
        ) : null}
      </span>
    );
  }

  const idx = item.title.toLowerCase().indexOf(item.highlight.toLowerCase());
  if (idx === -1) {
    return (
      <span
        className={`transition-colors duration-500 ${
          isActive ? 'text-white' : ''
        }`}
      >
        {item.title}
      </span>
    );
  }
  const before = item.title.slice(0, idx);
  const marked = item.title.slice(idx, idx + item.highlight.length);
  const after = item.title.slice(idx + item.highlight.length);
  return (
    <>
      <span
        className={`transition-colors duration-500 ${
          isActive ? 'text-white' : ''
        }`}
      >
        {before}
      </span>
      <span className={`font-bold transition-colors duration-500 ${isActive ? 'text-[#ff5d10]' : ''}`}>
        {marked}
      </span>
      <span
        className={`transition-colors duration-500 ${
          isActive ? 'text-white' : ''
        }`}
      >
        {after}
      </span>
    </>
  );
}

export default function HomeServicesSection() {
  const { language, t } = useI18n();
  const { homeServices } = getHomeData(language);
  const visibleServices = useMemo(() => {
    const list = Array.isArray(homeServices) ? homeServices : [];
    return list.filter((_, index) => index !== 2);
  }, [homeServices]);
  const hasTwoServices = visibleServices.length === 2;
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoPaused, setIsAutoPaused] = useState(false);
  const glowRef = useRef(null);
  const glowRafRef = useRef(null);
  const AUTO_HOVER_MS = 2800;

  const handleSectionMouseMove = (event) => {
    if (glowRafRef.current) return;
    const clientX = event.clientX;
    const clientY = event.clientY;
    const rect = event.currentTarget.getBoundingClientRect();
    glowRafRef.current = requestAnimationFrame(() => {
      glowRafRef.current = null;
      const x = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
      const y = Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100));
      if (glowRef.current) {
        glowRef.current.style.backgroundImage = `radial-gradient(circle at ${x}% ${y}%, rgba(255,93,16,0.08) 0%, transparent 40%)`;
      }
    });
  };

  useEffect(() => {
    if (!visibleServices.length || isAutoPaused) return undefined;
    const timer = setTimeout(() => {
      setActiveIndex((prev) => (prev + 1) % visibleServices.length);
    }, AUTO_HOVER_MS);

    return () => clearTimeout(timer);
  }, [AUTO_HOVER_MS, activeIndex, isAutoPaused, visibleServices.length]);

  useEffect(() => {
    setActiveIndex(0);
  }, [language, visibleServices.length]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.14,
        delayChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 28 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
    },
  };

  return (
    <section
      className="relative overflow-hidden bg-[var(--dash-blacker)] pt-40 pb-60"
      onMouseMove={handleSectionMouseMove}
    >
      <div className="absolute inset-0 opacity-20 bg-[url('/bg/5.webp')] bg-no-repeat bg-[size:100%_100%]" />
      <div className="pointer-events-none absolute inset-0 z-0">
        <div className="absolute left-0 top-0 h-full w-full bg-[radial-gradient(circle_at_15%_25%,rgba(255,93,16,0.08)_0%,transparent_40%)]" />
      </div>
      <div className="pointer-events-none absolute inset-0 z-1">
        <div
          ref={glowRef}
          className="absolute left-0 top-0 h-full w-full transition-[background-image] duration-150 ease-out"
          style={{ backgroundImage: 'radial-gradient(circle at 15% 25%, rgba(255,93,16,0.08) 0%, transparent 40%)' }}
        />
      </div>
      <div className="pointer-events-none absolute right-0 top-0 h-[600px] w-[600px] translate-x-1/4 -translate-y-1/2 rounded-full bg-[#ff5d10]/5 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 left-0 h-[500px] w-[500px] -translate-x-1/4 translate-y-1/2 rounded-full bg-blue-500/5 blur-[100px]" />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'radial-gradient(#ffffff 0.5px, transparent 0.5px)',
          backgroundSize: '40px 40px',
        }}
      />

      <div className="container relative z-10 mx-auto px-6 lg:px-12">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="mb-24 flex flex-col justify-between gap-8 md:flex-row md:items-end"
        >
          <motion.div key={`services-header-${language}`} variants={itemVariants}>
            <AnimatedEyebrow className="mb-6">{t('home.services.eyebrow')}</AnimatedEyebrow>
            <h2 className="mt-4 text-4xl font-light uppercase leading-tight text-white sm:text-5xl">
              <motion.span key={`services-title-line1-${language}`} variants={itemVariants} className="block">
                {t('home.services.titleLine1')}
              </motion.span>
              <motion.span
                key={`services-title-line2-${language}`}
                variants={itemVariants}
                className="font-black bg-gradient-to-r from-orange-400 via-[var(--dash-main)] to-orange-700 bg-clip-text text-transparent"
              >
                {t('home.services.titleLine2')}
              </motion.span>
            </h2>
          </motion.div>
          <motion.div key={`services-side-note-${language}`} variants={itemVariants} className="hidden lg:block">
            <p className="max-w-[260px] border-r-2 border-[var(--dash-main)] pr-8 text-right text-xs uppercase leading-loose tracking-widest text-slate-400">
              {t('home.services.sideNote')}
            </p>
          </motion.div>
        </motion.div>

        <motion.div
          key={`services-grid-${language}`}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
          className={`grid gap-10 md:grid-cols-1 ${hasTwoServices ? 'lg:grid-cols-2' : 'lg:grid-cols-3'}`}
        >
          {visibleServices.map((service, index) => {
            const isActive = activeIndex === index;
            return (
            <motion.article
              key={`${service.titleLine1 || service.title || 'service'}-${index}`}
              variants={itemVariants}
              onMouseEnter={() => {
                setIsAutoPaused(true);
                setActiveIndex(index);
              }}
              onMouseLeave={() => setIsAutoPaused(false)}
              className={`group relative flex h-full flex-col rounded-xl transition-all duration-700 ease-in-out ${
                !hasTwoServices && index === 1 ? 'lg:mt-12 lg:mb-12' : ''
              }`}
            >
              <div
                className={`relative flex h-full flex-col overflow-hidden rounded-t-xl border border-white/10 transition-all duration-500 group-hover:-translate-y-2 group-hover:border-[#ff5d10]/30 ${
                  isActive ? '-translate-y-2 border-[#ff5d10]/30' : ''
                }`}
              >
                {' '}
                <div className="relative h-96 w-full isolate overflow-hidden bg-[#111116]">
                  {/* <div className="pointer-events-none absolute inset-x-0 bottom-[-2px] top-0 z-10 bg-gradient-to-t from-[#111116] via-transparent to-transparent" /> */}
                  <ImageOnlySkeletonLoader
                    src={service.image}
                    mode="native"
                    alt={service.title}
                    containerClassName="h-full w-full"
                    imgClassName={
                      isActive
                        ? 'h-full w-full scale-110 object-cover opacity-100 transition duration-[1.5s] ease-out'
                        : 'h-full w-full scale-105 object-cover opacity-60 transition duration-[1.5s] ease-out group-hover:scale-110 group-hover:opacity-100'
                    }
                  />
                  <div
                    className={`absolute left-0 top-8 z-20 opacity-0 transition-all duration-500 group-hover:left-8 group-hover:opacity-100 ${
                      isActive ? 'left-8 opacity-100' : ''
                    }`}
                  >
                    <div
                      className={`flex h-14 w-14 items-center justify-center rounded-lg border border-white/10 bg-[#0a0a0c]/80 text-[var(--dash-black)] backdrop-blur-xl transition-all duration-500 group-hover:border-[var(--dash-other)] group-hover:bg-[var(--dash-other)] ${
                        isActive ? 'border-[var(--dash-other)] bg-[var(--dash-other)]' : ''
                      }`}
                    >
                      <span className="text-md font-semibold leading-none transition-transform duration-500">
                        0{index + 1}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="relative z-10 flex flex-grow flex-col bg-[var(--dash-blacker)] p-7 pt-7">
                  <h3 className={`mb-4 text-2xl font-light leading-tight transition-colors duration-500 ${isActive ? 'text-white' : 'text-white/90'}`}>
                    {renderTitleWithHighlight(service, isActive)}
                  </h3>
                  <p
                    className={
                      isActive
                        ? 'mb-6 flex-grow text-md font-light leading-relaxed text-slate-400 transition-colors'
                        : 'mb-6 flex-grow text-md font-light leading-relaxed text-slate-600 transition-colors group-hover:text-slate-400'
                    }
                  >
                    {service.description}
                  </p>
                  <Link
                    href="/services"
                    className="group/link mt-auto flex w-full items-center justify-between border-t border-white/[0.5] group-hover:border-[var(--dash-other)] pt-5 transition-colors"
                  >
                    <span
                      className={
                        isActive
                          ? 'text-[11px] font-bold uppercase tracking-[0.3em] text-white transition-colors'
                          : 'text-[11px] font-bold uppercase tracking-[0.3em] text-slate-500 transition-colors group-hover/link:text-white'
                      }
                    >
                      {t('home.services.discoverMore')}
                    </span>
                    <div className="relative h-5 w-8 overflow-hidden">
                      <ArrowRight
                        className={
                          isActive
                            ? 'absolute right-0 h-5 w-5 translate-x-0 text-[#ff5d10] opacity-100 transition-all duration-500'
                            : 'absolute right-0 h-5 w-5 translate-x-8 text-[#ff5d10] opacity-0 transition-all duration-500 group-hover/link:translate-x-0 group-hover/link:opacity-100'
                        }
                      />
                      <ArrowRight
                        className={
                          isActive
                            ? 'absolute right-0 h-5 w-5 translate-x-8 text-slate-600 opacity-0 transition-all duration-500'
                            : 'absolute right-0 h-5 w-5 text-slate-600 transition-all duration-500 group-hover/link:translate-x-8 group-hover/link:opacity-0'
                        }
                      />
                    </div>
                  </Link>
                </div>
                <div
                  className={`absolute bottom-0 left-0 z-20 h-[5px] w-0 bg-[#ff5d10] transition-all duration-700 group-hover:w-full ${
                    isActive ? 'w-full' : ''
                  }`}
                />
              </div>
            </motion.article>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
